<script setup lang="ts">
import { provideMediaProviders } from '@voix/composables/useMediaManagerInjections'
import { provide, ref, watchEffect } from 'vue'

import type { VoixMediaImage, VoixMediaVideo } from '@voix/types'
import TextInput from '@voix/components/chrome/controls/TextInput.vue'
import DropdownSelect from '@voix/components/chrome/controls/DropdownSelect.vue'
import MediaManager from './MediaManager.vue'
import { useImage } from '#imports'

// Width and height are optional. If they are not provided then they are null and
// we will ask the user to tell us what the width and height should be.
const props = defineProps<{
  width?: number
  height?: number
}>()

const emit = defineEmits(['selectMedia'])

const showMediaManager = ref(false)

const provider = provideMediaProviders()

const fieldConfiguration = {
  type: 'media',
  label: 'Image',
  breakpoints: {
    default: {
      height: 337,
      width: 600,
    },
  },
}

const width = ref<number | null>(props.width || null)
const height = ref<number | null>(props.height || null)

const settingWidth = ref<string | null>(null)
const settingHeight = ref<string | null>(null)
const settingCrop = ref<string | null>(null)

function setWidthHeight() {
  width.value = settingWidth.value ? Number.parseInt(settingWidth.value) : null
  height.value = settingHeight.value ? Number.parseInt(settingHeight.value) : null
}

watchEffect(() => {
  // when showMediaManager is selected we will reset the width and height
  if (showMediaManager.value) {
    width.value = props.width ? props.width : null
    height.value = props.width ? props.width : null
    settingCrop.value = null
  }
})

provide('fieldConfiguration', fieldConfiguration)

provide(
  'selectMedia',
  (breakpoint: string, media: VoixMediaImage | VoixMediaVideo) => {
    if (breakpoint && media) {
      const baseUrl = media.final_url ? media.final_url : media.url

      if (provider.value) {
        const img = useImage()

        const finalImage = img(
          baseUrl,
          {
            width: width.value || undefined,
            height: height.value || undefined,
            fit: settingCrop.value || undefined,
          },
          {
            provider: provider.value,
          },
        )

        emit('selectMedia', finalImage)

        showMediaManager.value = false
      }
    }
  },
)
</script>

<template>
  <div>
    <div v-if="!showMediaManager" @click="showMediaManager = true">
      <slot />
    </div>

    <div v-if="!width && showMediaManager">
      <div class="flex space-x-2">
        <TextInput id="settings-width" v-model="settingWidth" label="Width" />
        <TextInput id="settings-height" v-model="settingHeight" label="Height" />
        <DropdownSelect
          id="settings-crop"
          v-model="settingCrop"
          label="Crop"
          :options="[
            {
              label: `Cover`,
              value: 'crop',
              key: 'crop',
            },
            {
              label: `Contain`,
              value: 'clip',
              key: 'clip',
            },
            {
              label: `Edge Pixels`,
              value: 'clamp',
              key: 'clamp',
            },
          ]"
        />
      </div>
      <div class="mt-2 space-x-1">
        <button
          class="voix-admin-bg-light voix-admin-text-darkest duration-200 hover:voix-admin-bg-dark hover:voix-admin-text-lightest p-2 px-3 rounded-sm text-2xs font-bold uppercase h-full"
          @click="showMediaManager = false"
        >
          Cancel
        </button>
        <button
          class="voix-admin-bg-light voix-admin-text-darkest duration-200 hover:voix-admin-bg-dark hover:voix-admin-text-lightest p-2 px-3 rounded-sm text-2xs font-bold uppercase h-full"
          @click="setWidthHeight"
        >
          Set Image Size
        </button>
      </div>
    </div>

    <MediaManager v-if="width && showMediaManager" @close="showMediaManager = false" />
  </div>
</template>
